// Tisk technologického postupu pro X kusů.
// Recipe se rozloží rekurzivně (polotovary → suroviny) a vytiskne se A4.

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const fmtQty = (n) => {
  if (!Number.isFinite(Number(n))) return "0";
  return Number(n).toLocaleString("cs-CZ", { maximumFractionDigits: 4 });
};

const unitRatio = (product, mode) => {
  if (!mode || mode === "piece") return 1;
  if (mode === "package") {
    const n = Number(product?.package_size);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }
  const u = (product?.units || []).find((x) => x?.label === mode);
  const r = Number(u?.ratio);
  return Number.isFinite(r) && r > 0 ? r : 1;
};

const recipeOutputPieces = (product, recipe) => {
  const out = Number(recipe?.output_quantity || 1);
  return out * unitRatio(product, recipe?.output_unit);
};

/**
 * Rekurzivně rozloží recept na suroviny.
 * Vrací { materials: {material_id: qty}, polotovary: [{product_id, name, qty}] }
 */
const resolve = (productId, qtyPieces, products, recipes, visited = new Set()) => {
  if (visited.has(productId)) {
    throw new Error("Cyklická závislost v receptech (polotovar A → B → A)");
  }
  const next = new Set(visited);
  next.add(productId);
  const product = products.find((p) => p.id === productId);
  const recipe = recipes.find((r) => r.product_id === productId);
  if (!recipe) {
    throw new Error(
      `Polotovar '${product?.name || productId}' nemá definovaný technologický postup`
    );
  }
  const outPieces = recipeOutputPieces(product, recipe);
  const factor = qtyPieces / outPieces;
  const materials = {};
  const polotovary = [];
  for (const ing of recipe.ingredients || []) {
    const q = Number(ing.quantity || 0) * factor;
    if (ing.material_id) {
      materials[ing.material_id] = (materials[ing.material_id] || 0) + q;
    } else if (ing.product_id) {
      const child = resolve(ing.product_id, q, products, recipes, next);
      const childProd = products.find((p) => p.id === ing.product_id);
      polotovary.push({ product_id: ing.product_id, name: childProd?.name || ing.product_id, qty: q });
      for (const [mid, mq] of Object.entries(child.materials)) {
        materials[mid] = (materials[mid] || 0) + mq;
      }
      for (const sub of child.polotovary) polotovary.push(sub);
    }
  }
  return { materials, polotovary };
};

export function printRecipe({ product, recipe, products, recipes, materials, qtyPieces }) {
  if (!product || !recipe) {
    throw new Error("Chybí výrobek nebo technologický postup pro tisk");
  }
  const targetQty = Math.max(1, Number(qtyPieces) || 1);
  const resolved = resolve(product.id, targetQty, products, recipes);
  const outPieces = recipeOutputPieces(product, recipe);
  const batches = targetQty / outPieces;

  // Sestav materiálovou tabulku
  const matRows = Object.entries(resolved.materials)
    .map(([mid, qty]) => {
      const m = materials.find((x) => x.id === mid);
      return {
        name: m?.name || mid,
        unit: m?.unit || "",
        qty,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "cs"));

  // Přímé ingredience (1. úroveň)
  const directRows = (recipe.ingredients || []).map((ing) => {
    const factor = targetQty / outPieces;
    const qty = Number(ing.quantity || 0) * factor;
    if (ing.material_id) {
      const m = materials.find((x) => x.id === ing.material_id);
      return { type: "Surovina", name: m?.name || ing.material_id, unit: m?.unit || "", qty };
    }
    const p = products.find((x) => x.id === ing.product_id);
    return { type: "Polotovar", name: p?.name || ing.product_id, unit: "ks", qty };
  });

  const dt = new Date().toLocaleString("cs-CZ");
  const outputUnit = recipe.output_unit || "piece";
  const outUnitLabel =
    outputUnit === "piece" ? "ks" : outputUnit === "package" ? product.package_label || "balení" : outputUnit;

  const html = `<!doctype html>
<html lang="cs">
<head>
<meta charset="utf-8" />
<title>Technologický postup — ${escapeHtml(product.name)} (${fmtQty(targetQty)} ks)</title>
<style>
  @page { size: A4; margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #2a2520; margin: 0; padding: 0; font-size: 12px; line-height: 1.5; }
  .h { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #3A2D27; padding-bottom: 12px; margin-bottom: 20px; }
  .h .ttl { font-size: 22px; font-weight: 700; color: #3A2D27; }
  .h .eb { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #57534E; margin-bottom: 4px; }
  .h .meta { text-align: right; font-size: 11px; color: #57534E; }
  .panel { background: #F9F6F0; border-radius: 8px; padding: 14px 18px; margin-bottom: 16px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .panel .it { }
  .panel .lab { font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #57534E; margin-bottom: 3px; }
  .panel .val { font-size: 16px; font-weight: 600; color: #3A2D27; }
  h2 { font-size: 14px; color: #3A2D27; margin: 20px 0 8px; border-left: 3px solid #D4A373; padding-left: 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { text-align: left; padding: 8px 6px; background: #F9F6F0; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #57534E; border-bottom: 1px solid #E5E1D8; }
  td { padding: 8px 6px; border-bottom: 1px solid #EFEAE0; }
  td.r, th.r { text-align: right; }
  .badge { display: inline-block; font-size: 9px; padding: 2px 6px; border-radius: 99px; background: #D4A37326; color: #8a6622; border: 1px solid #D4A37366; }
  .muted { color: #57534E; font-size: 10px; }
  .foot { margin-top: 30px; padding-top: 12px; border-top: 1px solid #E5E1D8; font-size: 10px; color: #57534E; display: flex; justify-content: space-between; }
  .sig { margin-top: 24px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
  .sig .line { border-top: 1px solid #2a2520; padding-top: 4px; font-size: 10px; color: #57534E; }
</style>
</head>
<body>
  <div class="h">
    <div>
      <div class="eb">Technologický postup</div>
      <div class="ttl">${escapeHtml(product.name)}</div>
    </div>
    <div class="meta">
      <div><strong>${fmtQty(targetQty)} ks</strong> · vytištěno ${escapeHtml(dt)}</div>
      <div>${escapeHtml(batches.toFixed(2))}× dávka</div>
    </div>
  </div>

  <div class="panel">
    <div class="it"><div class="lab">Cílové množství</div><div class="val">${fmtQty(targetQty)} ks</div></div>
    <div class="it"><div class="lab">1 dávka =</div><div class="val">${fmtQty(recipe.output_quantity)} ${escapeHtml(outUnitLabel)}</div></div>
    <div class="it"><div class="lab">Počet dávek</div><div class="val">${batches.toFixed(2)}×</div></div>
  </div>

  <h2>1. Ingredience pro tuto výrobu</h2>
  <table>
    <thead>
      <tr>
        <th>Typ</th>
        <th>Položka</th>
        <th class="r">Množství</th>
      </tr>
    </thead>
    <tbody>
      ${directRows
        .map(
          (r) => `
        <tr>
          <td><span class="badge">${escapeHtml(r.type)}</span></td>
          <td>${escapeHtml(r.name)}</td>
          <td class="r mono"><strong>${fmtQty(r.qty)}</strong> ${escapeHtml(r.unit)}</td>
        </tr>`
        )
        .join("")}
    </tbody>
  </table>

  ${resolved.polotovary.length > 0
    ? `
  <h2>2. Polotovary k výrobě (kaskáda)</h2>
  <table>
    <thead><tr><th>Polotovar</th><th class="r">Množství (ks)</th></tr></thead>
    <tbody>
      ${resolved.polotovary
        .map(
          (p) => `
        <tr>
          <td>${escapeHtml(p.name)}</td>
          <td class="r mono"><strong>${fmtQty(p.qty)}</strong> ks</td>
        </tr>`
        )
        .join("")}
    </tbody>
  </table>
  `
    : ""}

  <h2>${resolved.polotovary.length > 0 ? "3" : "2"}. Celková spotřeba surovin (vč. polotovarů)</h2>
  <table>
    <thead>
      <tr>
        <th>Surovina</th>
        <th class="r">Celkem</th>
      </tr>
    </thead>
    <tbody>
      ${matRows
        .map(
          (r) => `
        <tr>
          <td>${escapeHtml(r.name)}</td>
          <td class="r mono"><strong>${fmtQty(r.qty)}</strong> ${escapeHtml(r.unit)}</td>
        </tr>`
        )
        .join("")}
    </tbody>
  </table>

  <div class="sig">
    <div class="line">Vyrobil (jméno, podpis, datum)</div>
    <div class="line">Kontroloval (jméno, podpis, datum)</div>
  </div>

  <div class="foot">
    <span>Caféčko ERP — technologický postup</span>
    <span>Strana 1/1</span>
  </div>

  <script>
    window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 250); });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) {
    throw new Error("Vyskakovací okno bylo zablokované prohlížečem");
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
